import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../api/api';
import VenueLayoutEditor from '../components/VenueLayoutEditor';
import { VenueLayout, validateLayout } from '../utils/venueLayout';
import { clearBookingLayoutAccess, peekBookingLayoutAccess } from '../utils/bookingLayoutAccess';
import { bookingAccessHeaders } from '../utils/bookingAccessHeaders';

interface Venue { id: number; name: string }
interface Booking { id: number; venueName: string; eventDate: string; customerName: string }
const blank = (): VenueLayout => ({
  document: { width: 15, height: 10, capacity: 100, elements: [] },
  version: 0, sourceTemplateVersion: null, customerEditingEnabled: false, updatedAtUtc: null,
});
const message = (error: unknown) => axios.isAxiosError<{ message?: string }>(error)
  ? error.response?.data?.message ?? 'Не вдалося завантажити або зберегти план.'
  : error instanceof Error ? error.message : 'Не вдалося обробити план.';

export default function VenueLayoutPage({ customer = false }: { customer?: boolean }) {
  const [initialAccess] = useState(() => customer ? peekBookingLayoutAccess() : undefined);
  const [mode, setMode] = useState<'templates' | 'bookings'>('templates');
  const [id, setId] = useState(initialAccess ? String(initialAccess.bookingId) : '');
  const [token, setToken] = useState(initialAccess?.customerAccessToken ?? '');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [layout, setLayout] = useState<VenueLayout>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dirty, setDirty] = useState(false);
  const generation = useRef(0);
  const path = customer ? `/venue-layouts/customer/bookings/${id}` : `/venue-layouts/${mode}/${id}`;
  useEffect(() => { if (customer) clearBookingLayoutAccess(); }, [customer]);
  useEffect(() => {
    if (customer) return;
    const controller = new AbortController();
    Promise.all([api.get<Venue[]>('/EventVenues', { signal: controller.signal }),
      api.get<Booking[]>('/event-venue-bookings/admin', { signal: controller.signal })])
      .then(([v, b]) => { setVenues(v.data); setBookings(b.data); })
      .catch(e => { if (!axios.isCancel(e)) setError(message(e)); });
    return () => controller.abort();
  }, [customer]);
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
  const reset = () => { generation.current++; setLayout(undefined); setDirty(false); setError(''); setNotice(''); };
  const mayDiscard = () => !dirty || window.confirm('Відкинути незбережені зміни плану?');
  const load = async (initialize = false) => {
    if (!id || (customer && !token) || !mayDiscard()) return;
    const current = ++generation.current;
    setBusy(true); setError(''); setNotice('');
    try {
      const response = initialize
        ? await api.post<VenueLayout>(`${path}/initialize`)
        : await api.get<VenueLayout>(path, customer
          ? { headers: bookingAccessHeaders(token, path) }
          : undefined);
      if (current === generation.current) { setLayout(response.data); setDirty(false); }
    } catch (e) {
      if (current === generation.current) { setLayout(undefined); setError(message(e)); }
    } finally { if (current === generation.current) setBusy(false); }
  };
  const save = async () => {
    if (!layout || validateLayout(layout.document)) return;
    const current = ++generation.current;
    setBusy(true); setError(''); setNotice('');
    try {
      const response = await api.put<VenueLayout>(path, {
        document: layout.document, version: layout.version, customerEditingEnabled: layout.customerEditingEnabled,
      }, customer ? { headers: bookingAccessHeaders(token, path) } : undefined);
      if (current === generation.current) { setLayout(response.data); setDirty(false); setNotice('План збережено.'); }
    } catch (e) { if (current === generation.current) setError(message(e)); }
    finally { if (current === generation.current) setBusy(false); }
  };
  return <main className="venue-layout-page">
    <h1>{customer ? 'План вашого залу' : 'Шаблони та плани залів'}</h1>
    <p>План не змінює меню, залишки, оплату або бронювання часу. Збережені копії не оновлюються разом із шаблоном.</p>
    <div className="layout-fields">
      {!customer && <label>Редагувати<select disabled={busy} value={mode} onChange={e => {
        if (mayDiscard()) { reset(); setMode(e.target.value as typeof mode); setId(''); }
      }}><option value="templates">Шаблон залу</option><option value="bookings">План бронювання</option></select></label>}
      {customer ? <>
        <label>Номер бронювання<input disabled={busy} type="number" min="1" step="1" value={id} onChange={e => {
          if (mayDiscard()) { reset(); setId(e.target.value); }
        }} /></label>
        <label>Приватний код доступу<input disabled={busy} type="password" autoComplete="off" value={token}
          onChange={e => { if (mayDiscard()) { reset(); setToken(e.target.value); } }} /></label>
      </> : <label>{mode === 'templates' ? 'Зал' : 'Бронювання'}<select disabled={busy} value={id} onChange={e => {
        if (mayDiscard()) { reset(); setId(e.target.value); }
      }}><option value="">Оберіть</option>
        {mode === 'templates' ? venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)
          : bookings.map(b => <option key={b.id} value={b.id}>#{b.id} — {b.eventDate}, {b.venueName}, {b.customerName}</option>)}
      </select></label>}
      <button disabled={busy || !id || (customer && !token)} onClick={() => load()}>Завантажити план</button>
      {!customer && <button disabled={busy || !id} onClick={() => {
        if (mode === 'bookings') load(true);
        else if (mayDiscard()) { setLayout(blank()); setDirty(true); setError(''); setNotice('Новий шаблон. Існуючий шаблон не буде перезаписано без завантаження його версії.'); }
      }}>{mode === 'templates' ? 'Новий шаблон' : 'Створити копію шаблону'}</button>}
    </div>
    {customer && <p>Введіть код із підтвердження бронювання. Без коду доступ неможливий. Код не зберігається у браузері.</p>}
    {error && <p role="alert">{error}</p>}
    <p role="status">{busy ? 'Обробка…' : notice}</p>
    {layout && <>
      <p>Версія: {layout.version}. {layout.sourceTemplateVersion !== null && `Копія шаблону версії ${layout.sourceTemplateVersion}.`}</p>
      {!customer && mode === 'bookings' && <label>
        <input type="checkbox" disabled={busy} checked={layout.customerEditingEnabled} onChange={e => {
          setLayout({ ...layout, customerEditingEnabled: e.target.checked }); setDirty(true);
        }} /> Дозволити замовнику редагувати цей план
      </label>}
      <VenueLayoutEditor key={path} value={layout.document} disabled={busy || (customer && !layout.customerEditingEnabled)}
        hallEditable={!customer} onChange={document => { setLayout({ ...layout, document }); setDirty(true); }} />
      <div className="layout-actions">
        <button disabled={busy || !dirty || !!validateLayout(layout.document) || (customer && !layout.customerEditingEnabled)}
          onClick={save}>Зберегти план</button>
        <button disabled={busy} onClick={() => load()}>Перезавантажити актуальну версію</button>
      </div>
    </>}
  </main>;
}
